// Takvim yardımcıları: tarih matematiği (yerel saat, hafta Pazartesi'den başlar), aralıklar,
// etkinlik yerleşimi ve resmî tatiller.

export const HOUR_PX = 56;
export const MIN_EVENT_MINUTES = 25; // çok kısa etkinlikler okunabilsin diye en az bu kadar yükseklik

export const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
export const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
export const WEEKDAYS_SHORT = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
export const WEEKDAYS_LONG = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

// Takvimler = etkinlik renkleri (veritabanında `color` alanı)
export const CALENDARS = [
  { id: 'blue', name: 'Kişisel', dot: 'bg-blue-500', box: 'bg-blue-100 border-blue-500 text-blue-900 dark:bg-blue-500/25 dark:text-blue-100', accent: 'accent-blue-500' },
  { id: 'green', name: 'İş', dot: 'bg-emerald-500', box: 'bg-emerald-100 border-emerald-500 text-emerald-900 dark:bg-emerald-500/25 dark:text-emerald-100', accent: 'accent-emerald-500' },
  { id: 'purple', name: 'Toplantılar', dot: 'bg-violet-500', box: 'bg-violet-100 border-violet-500 text-violet-900 dark:bg-violet-500/25 dark:text-violet-100', accent: 'accent-violet-500' },
  { id: 'amber', name: 'Hatırlatıcılar', dot: 'bg-amber-500', box: 'bg-amber-100 border-amber-500 text-amber-900 dark:bg-amber-500/25 dark:text-amber-100', accent: 'accent-amber-500' },
  { id: 'red', name: 'Önemli', dot: 'bg-red-500', box: 'bg-red-100 border-red-500 text-red-900 dark:bg-red-500/25 dark:text-red-100', accent: 'accent-red-500' },
];
export const calendarOf = (color) => CALENDARS.find((c) => c.id === color) || CALENDARS[0];

// ---- Tarih matematiği ----
export const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
export const startOfWeek = (d) => addDays(startOfDay(d), -((d.getDay() + 6) % 7)); // Pazartesi
export const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
export const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const minutesOfDay = (d) => d.getHours() * 60 + d.getMinutes();

export const getRange = (view, cursor) => {
  if (view === 'day') return { start: startOfDay(cursor), end: addDays(cursor, 1) };
  if (view === 'week') { const s = startOfWeek(cursor); return { start: s, end: addDays(s, 7) }; }
  if (view === 'month') {
    const s = startOfWeek(new Date(cursor.getFullYear(), cursor.getMonth(), 1));
    return { start: s, end: addDays(s, 42) };
  }
  return { start: startOfDay(cursor), end: addDays(cursor, 30) }; // agenda
};

export const monthWeeks = (cursor) => {
  const { start } = getRange('month', cursor);
  const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const weeks = Math.round((startOfWeek(last).getTime() - start.getTime()) / 86400000 / 7) + 1; // yaz saati kaymasına dayanıklı
  return Array.from({ length: weeks }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(start, w * 7 + i)));
};

export const formatTitle = (view, cursor) => {
  if (view === 'month') return `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;
  if (view === 'day') return `${cursor.getDate()} ${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}, ${WEEKDAYS_LONG[(cursor.getDay() + 6) % 7]}`;
  if (view === 'week') {
    const s = startOfWeek(cursor); const e = addDays(s, 6);
    if (s.getMonth() === e.getMonth()) return `${s.getDate()} – ${e.getDate()} ${MONTHS[e.getMonth()]} ${e.getFullYear()}`;
    if (s.getFullYear() === e.getFullYear()) return `${s.getDate()} ${MONTHS_SHORT[s.getMonth()]} – ${e.getDate()} ${MONTHS_SHORT[e.getMonth()]} ${e.getFullYear()}`;
    return `${s.getDate()} ${MONTHS_SHORT[s.getMonth()]} ${s.getFullYear()} – ${e.getDate()} ${MONTHS_SHORT[e.getMonth()]} ${e.getFullYear()}`;
  }
  const e = addDays(cursor, 29);
  return `${cursor.getDate()} ${MONTHS_SHORT[cursor.getMonth()]} – ${e.getDate()} ${MONTHS_SHORT[e.getMonth()]} ${e.getFullYear()}`;
};

export const shiftCursor = (view, cursor, dir) => {
  if (view === 'month') return new Date(cursor.getFullYear(), cursor.getMonth() + dir, 1);
  if (view === 'week') return addDays(cursor, 7 * dir);
  if (view === 'agenda') return addDays(cursor, 30 * dir);
  return addDays(cursor, dir);
};

export const formatTime = (d) => d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
export const formatDayLong = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${WEEKDAYS_LONG[(d.getDay() + 6) % 7]}`;
export const tzLabel = () => {
  const offset = -new Date().getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return `GMT${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
};

// Dar sütunlar için kısa biçim: GMT+3, GMT+5:30
export const tzShort = () => tzLabel().replace(/^GMT([+-])0?(\d+):00$/, 'GMT$1$2').replace(/^GMT([+-])0?(\d+):/, 'GMT$1$2:');

// ---- Etkinlikler ----
export const parseEvent = (raw) => ({ ...raw, start: new Date(raw.start_time), end: new Date(raw.end_time) });

// Etkinliğin bu günle çakışan kısmı (dakika cinsinden) ya da null
const segmentOn = (event, day) => {
  const dayStart = startOfDay(day).getTime();
  const dayEnd = addDays(day, 1).getTime();
  if (event.end.getTime() <= dayStart || event.start.getTime() >= dayEnd) return null;
  const s = Math.max(event.start.getTime(), dayStart);
  const e = Math.min(event.end.getTime(), dayEnd);
  return { event, startMin: Math.round((s - dayStart) / 60000), endMin: Math.round((e - dayStart) / 60000) };
};

export const timedEventsOn = (events, day) =>
  events.filter((e) => !e.all_day).map((e) => segmentOn(e, day)).filter(Boolean);

export const allDayEventsOn = (events, day) =>
  events.filter((e) => e.all_day && segmentOn(e, day));

// Çakışan etkinlikleri yan yana sütunlara dağıt (Google Takvim benzeri)
export const layoutSegments = (segments) => {
  const sorted = segments
    .map((s) => ({ ...s, endMin: Math.max(s.endMin, s.startMin + MIN_EVENT_MINUTES) }))
    .sort((a, b) => a.startMin - b.startMin || b.endMin - a.endMin);
  const clusters = [];
  let current = [];
  let currentEnd = -1;
  sorted.forEach((seg) => {
    if (current.length && seg.startMin >= currentEnd) { clusters.push(current); current = []; currentEnd = -1; }
    current.push(seg);
    currentEnd = Math.max(currentEnd, seg.endMin);
  });
  if (current.length) clusters.push(current);

  const placed = [];
  clusters.forEach((cluster) => {
    const columnEnds = [];
    cluster.forEach((seg) => {
      let col = columnEnds.findIndex((end) => end <= seg.startMin);
      if (col === -1) { col = columnEnds.length; columnEnds.push(0); }
      columnEnds[col] = seg.endMin;
      seg.col = col;
    });
    cluster.forEach((seg) => placed.push({ ...seg, cols: columnEnds.length }));
  });
  return placed;
};

// Hafta görünümünde tüm gün şeridi: günlere yayılan etkinlikler için sütun aralığı + satır
export const layoutAllDayRow = (events, days) => {
  const items = [];
  events.filter((e) => e.all_day).forEach((e) => {
    const first = days.findIndex((d) => segmentOn(e, d));
    if (first === -1) return;
    let last = first;
    days.forEach((d, i) => { if (segmentOn(e, d)) last = i; });
    items.push({ event: e, startCol: first, endCol: last });
  });
  items.sort((a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol));
  const rowEnds = [];
  items.forEach((item) => {
    let row = rowEnds.findIndex((end) => end < item.startCol);
    if (row === -1) { row = rowEnds.length; rowEnds.push(-1); }
    rowEnds[row] = item.endCol;
    item.row = row;
  });
  return { items, rows: rowEnds.length };
};

// ---- Resmî tatiller (yalnızca sabit tarihli; dinî bayramlar her yıl değiştiği için dahil değildir) ----
const FIXED_HOLIDAYS = [
  [0, 1, 'Yılbaşı'],
  [3, 23, 'Ulusal Egemenlik ve Çocuk Bayramı'],
  [4, 1, 'Emek ve Dayanışma Günü'],
  [4, 19, "Atatürk'ü Anma, Gençlik ve Spor Bayramı"],
  [6, 15, 'Demokrasi ve Millî Birlik Günü'],
  [7, 30, 'Zafer Bayramı'],
  [9, 29, 'Cumhuriyet Bayramı'],
];
export const holidaysByDate = (years) => {
  const map = {};
  years.forEach((y) => FIXED_HOLIDAYS.forEach(([m, d, name]) => { map[dateKey(new Date(y, m, d))] = name; }));
  return map;
};
